<template>
  <v-form ref="CONTRAT_SALARIE" @submit.prevent="sectionSave" lazy-validation class="section">
    <div>
      <section-header v-if="!hideTitle"
                      :ressource="ressource"
                      :isExtended="extended"
                      :dialog="dialog"
                      :picto="picto()"
                      @extended=setExtended
                      @closeDialog=closeDialog
                      ref="CONTRAT_SALARIE">
      </section-header>
      <div v-if="extended" :class="dialog ? 'p-15' : 'py-2'">
        <graph-control v-if="isDisplayed('GRAPH_CONTROL')"
                       ref="GRAPH_CONTROL"
                       :obj=contrat
                       :objType="objType"
                       :toggle="toggleGraph"
                       v-model:value=graphAnswers
                       :disabled="readOnly('GRAPH')"
                       @status="statusGraph"
                       @setMinimum="setMinimum"
                       @hasGraph="hasGraph">
        </graph-control>
        <emploi-select v-if="isDisplayed('EMPLOI_ID')"
                       ref="EMPLOI_ID"
                       :theme="theme"
                       v-model:value=emploiId
                       :contrat=contrat
                       :moduleId=moduleId
                       :ressource=ressourceEmploi
                       libelle='Emploi'
                       @status=statusEmploiId
                       :toggleReset=resetEmploiSelect
                       :required=true
                       :contextId=contrat.id
                       :readOnly="readOnly('EMPLOI_ID')">
        </emploi-select>
        <convention-clause-miroir v-if="isDisplayed('CONVENTION_ID')"
                                  ref="CONVENTION_ID"
                                  :theme="theme"
                                  v-model:value=conventionAppliqueeId
                                  :contrat=contrat
                                  :moduleId=moduleId
                                  :ressource=ressourceConvention
                                  libelle='Convention clause miroir'
                                  @status=statusConventionAppliqueeId
                                  :toggleReset=resetConventionClauseMiroir
                                  :required=true
                                  :contextId=contrat.id
                                  :readOnly="readOnly('CONVENTION_ID')">
        </convention-clause-miroir>
        <salarie-summary v-if="isDisplayed('SALARIE_ID')"
                         ref="SALARIE_ID"
                         :theme="theme"
                         v-model:value=salarieId
                         :moduleId=moduleId
                         libelle='Salarié'
                         @status=statusSalarieId
                         :autoClear=true
                         :required=false
                         :contextId=contrat.id
                         :ressource=ressourceSalarieSummary
                         :owner=owner
                         :toggleReset="toggleSalarieSummary"
                         :sectionName="sectionName"
                         :readOnly="readOnly('SALARIE_ID')">
        </salarie-summary>
        <contrat-exoEtranger v-if="isDisplayed('EXO_ETRANGER')"
                             ref="CI98"
                             :theme="theme"
                             :showMissing="showMissing"
                             v-model:value=exoEtranger
                             @status=statusExoEtranger
                             :required=true
                             :contextId=contrat.id
                             :readOnly="readOnly('EXO_ETRANGER')"
                             :objType=objType
                             :fiche=fiche
                             :sectionName="sectionName">
        </contrat-exoEtranger>
      </div>
    </div>
    <div v-if="(extended || updatedAndValid) && saveButton" class="d-flex flex-row-reverse">
      <loader-button class="ms-2"
                     type="submit"
                     :loading="saveLoading"
                     :label="saveLabel"
                     :disabled="!updatedAndValid">
      </loader-button>
      <loader-button v-if="applyExoEtranger"
                     class="ms-2"
                     type="button"
                     :loading="a1Loading"
                     :label="a1Label"
                     outlined=true
                     :disabled="a1Loading"
                     @callback="a1Action">
      </loader-button>
      <loader-button v-if="hasFormulaireA1"
                     class="ms-2"
                     type="button"
                     :loading="a1Loading"
                     :label="a1Update"
                     outlined=true
                     :disabled="a1Loading"
                     @callback="a1Download">
      </loader-button>
    </div>
    <div v-else class="d-flex flex-row-reverse">
      <loader-button class="mt-2 ms-2"
                     type="button"
                     :loading="modifying"
                     :disabled="disabled"
                     :outlined="outlined"
                     :label="modifyLabel"
                     @callback="modify">
      </loader-button>
    </div>
  </v-form>
</template>

<script lang="ts">
  import { defineComponent } from "vue";
  import CrudContrat from '@/generated/cruds/CrudContrat';
  import { GenControl, GenSection, RessourceGenControl, RessourceGenSection } from '@/generated/ressources/generatedRessources';
  import { FwkButtons, RessourceFwkButton } from '@/ressources/globalRessources';
  import SELECT_VALUE from '@/framework/base/components/SelectField.vue'
  import Contrat, { ContratStatuts } from '@/generated/classes/Contrat';
  import { storeContact, storeDocument, storeEtablissement } from '@/generated/stores';
  import DialogHubManager from "@/framework/dialog/helpers/DialogHubManager";
  import Contact from '@/extend/classes/Contact';
  import Owner from '@/extend/classes/Owner';
  import { ObjTypes } from '@/generated/classes/ObjTypes';
  import { Sections } from '@/generated/classes/AppSchema';
  import { storeSalarie } from '@/generated/stores';
  import { FieldStatus } from '@/framework/base/components/FieldInfo';
  import Document from '@/extend/classes/Document';
  import { ObjType } from '@/generated/classes/Document';

  export default defineComponent({
    components: {},
    extends: CrudContrat,
    props: {
      hideTitle: {
        type: Boolean
      }
    },
    mounted(): void {
      storeDocument.registerDocument().then((res) => {
        const objType = new ObjType()
        objType.type = ObjTypes.FORMULAIRE_A1;
        objType.id = this.contrat.id;
        Document.getByObjType(objType).then((doc) => {
          if (doc != null) {
            const docA1 = storeDocument.getDocument(doc.id);
          }
        });
      });
      DialogHubManager.waitAnswer('createSalarie', this.createSalarie);
    },
    data() {
      return {
        sectionName: Sections.CONTRAT_SALARIE,
        modifying: false,
        useGraph: false,
        modifyLabel: RessourceFwkButton(FwkButtons.MODIFIER).label,
        lastEmploi: 0,
        emploiReset: false,
        clauseMirroirReset: false,
        lastContratId: 0,
        lastConventionId: 0,
        toggleSalarieSummary: false,
        a1Loading: false,
        docLoaded: false
      };
    },
    computed: {
      saveLabel() {
        return RessourceFwkButton(FwkButtons.ENREGISTRER).label;
      },
      a1Label() {
        if (this.hasFormulaireA1) {
          return RessourceFwkButton(FwkButtons.DISPLAY_A1).label;
        } else {
          return RessourceFwkButton(FwkButtons.DOWMLOAD_A1).label;
        }
      },
      a1Update() {
        return RessourceFwkButton(FwkButtons.UPDATE_A1).label;
      },
      ressourceEmploi() {
        return RessourceGenControl(GenControl.CONTRAT_SALARIE_EMPLOI_SELECT);
      },
      ressourceConvention() {
        return RessourceGenControl(GenControl.CONTRAT_SALARIE_CONVENTION_CLAUSE_MIROIR);
      },
      ressourceSalarieSummary() {
        return RessourceGenControl(GenControl.CONTRAT_SALARIE_SALARIE_SUMMARY);
      },
      ressource() {
        return RessourceGenSection(GenSection.CONTRAT_SALARIE);
      },
      outlined(): boolean {
        return this.contrat.statut == ContratStatuts.SUBMITTED ||
          this.contrat.statut == ContratStatuts.APPROUVED ||
          this.contrat.statut == ContratStatuts.SIGNED ||
          this.contrat.statut == ContratStatuts.CHECKED;
      },
      disabled(): boolean {
        return this.contrat.statut == ContratStatuts.CANCELLED;
      },
      owner(): Owner {
        const owner = new Owner();
        owner.objType = ObjTypes.CONTRAT;
        owner.objId = this.contrat.id;
        return owner;
      },
      sameConvention(): boolean {
        const contratConvention = this.contrat.conventionAppliqueeId;
        const etablissement = storeEtablissement.getEtablissement(this.contrat.etablissementId);
        return this.contrat.emploiId <= 0 || etablissement.conventionId == contratConvention;
      },
      resetEmploiSelect: {
        get(): boolean {
          if (this.lastEmploi != 0 && this.contrat.emploiId == 0) {
            this.resetEploi();
          }
          return this.emploiReset
        },
        set(value: boolean) {
          this.emploiReset = value;
        }
      },
      resetConventionClauseMiroir: {
        get(): boolean {
          return this.clauseMirroirReset
        },
        set(value: boolean) {
          this.clauseMirroirReset = value;
        }
      },
      fields(): string[] {
        return ['EMPLOI_ID', 'CONVENTION_ID', 'SALARIE_ID', 'EXO_CHOMAHE', 'GRAPH_CONTROL'];
      },
      fiscaliteEtranger(): boolean {
        if (this.contrat.salarieId > 0 || this.exoEtranger == true) {
          const salarie = storeSalarie.getSalarie(this.contrat.salarieId);
          return salarie.fiscalite == 4 || this.exoEtranger == true;
        }
        return false;
      },
      applyExoEtranger(): boolean {
        return this.contrat.exoEtranger == true;
      },
      hasFormulaireA1(): boolean {
        const allDoc = storeDocument.listDocument;
        const docA1 = storeDocument.listDocument.filter(a => a.objType == ObjTypes.FORMULAIRE_A1 && a.objId == this.contrat.id);
        return docA1.length > 0;
      }
    },

    methods: {
      resetEploi() {
        this.lastEmploi = 0 && this.contrat.emploiId;
        this.emploiReset = !this.emploiReset;
      },
      hasGraph(value: boolean) {
        this.useGraph = value;
      },
      fieldUpdated(field: string, value: unknown) {
        if (field == 'EMPLOI_ID') {
          if (value != SELECT_VALUE) {
            const emploiId = value as number;
            if (emploiId != 1 || this.contrat.emploiId == 0 || emploiId != this.lastEmploi) {
              if (emploiId != this.lastEmploi) {
                this.lastEmploi = emploiId;
              }
              this.contrat.conventionAppliqueeId = 0;
              this.resetConventionClauseMiroir = !this.resetConventionClauseMiroir;
            }
          }
        }
      },
      readOnly(value: string): boolean {
        if (this.lastContratId != this.contrat.id) {
          if (this.lastContratId > 0) {
            const etablissement = storeEtablissement.getEtablissement(this.contrat.etablissementId);
            if (this.lastConventionId != etablissement.conventionId) {
              if (this.lastConventionId > 0) {
                this.resetEmploiSelect = !this.resetEmploiSelect;
              }
              this.lastConventionId = etablissement.conventionId;
            }
          }
          this.lastContratId = this.contrat.id;
        }

        let res = false;
        const readOnly = !this.contrat.canUpdate || (this.contrat.statut >= ContratStatuts.SUBMITTED && this.contrat.statut != ContratStatuts.MODIFIED);
        if (value == 'EMPLOI_ID') {
          res = this.contrat.id == 0 || readOnly || this.fiche == undefined;
        } else if (value == 'CONVENTION_ID') {
          res = this.contrat.id == 0 || this.contrat.emploiId <= 0 || readOnly;
        } else if (value == 'SALARIE_ID') {
          res = this.contrat.id == 0 || this.contrat.conventionAppliqueeId <= 0 || readOnly || this.contrat.submittedAt != null;
        } else if (value == 'EXO_ETRANGER') {
          res = readOnly && this.contrat.exoEtranger != null;
        } else if (value == 'GRAPH_CONTROL') {
          res = readOnly;
        }
        return res;
      },
      isDisplayed(value: string): boolean {
        let res = false;
        if (value == 'EMPLOI_ID') {
          res = !this.useGraph && this.fiche != undefined;
        } else if (value == 'CONVENTION_ID') {
          res = !this.useGraph && !this.sameConvention;
        } else if (value == 'SALARIE_ID') {
          res = true;
        } else if (value == 'EXO_ETRANGER') {
          res = this.fiscaliteEtranger;
        } else if (value == 'GRAPH_CONTROL') {
          res = this.useGraph;
        }
        return res;
      },
      modify(): void {
        this.modifying = true;
        Contrat.modify(this.contrat).then(() => {
          this.modifying = false;
          this.$emit('updated');
        }).catch(() => {
          this.modifying = false;
        });
      },
      picto(): string {
        return 'SectionPictoSalarie'
      },
      createSalarie(obj: unknown) {
        const contact = obj as Contact;
        const salarie = storeSalarie.getSalarie(contact.id);
        if (this.contrat.salarieId != salarie.id) {
          this.toggleSalarieSummary = !this.toggleSalarieSummary
          this.contrat.salarieId = salarie.id;
          this.statusSalarieId(new FieldStatus(false, true, this.contrat.id))
        }
      },
      sectionSave(): void {
        return this.save()
      },
      a1Action(): void {
        this.a1Loading = true;
        if (this.hasFormulaireA1) {
          Contrat.displayA1(this.contrat).then(() => {
            this.a1Loading = false;
          }).catch(() => {
            this.a1Loading = false;
          });
        } else {
          this.a1Download();
        }
      },
      a1Download(): void {
        this.a1Loading = true;
        Contrat.downloadA1(this.contrat).then(() => {
          this.a1Loading = false;
        }).catch(() => {
          this.a1Loading = false;
        });
      }
    }
  })

</script>
